import { Type, type Static } from "@sinclair/typebox";
import { ProjectIdSchema, UuidSchema } from "./identifiers.ts";

export const FILE_SPACE_SCHEMA_VERSION = "1.0" as const;

const TimestampSchema = Type.String({ format: "date-time" });

export const FileSpaceSchema = Type.Object(
  {
    schemaVersion: Type.Literal(FILE_SPACE_SCHEMA_VERSION),
    fileSpaceId: UuidSchema,
    name: Type.String({ minLength: 1, maxLength: 256 }),
    openCloudSpaceId: Type.String({ minLength: 1, maxLength: 512 }),
    automationEnabled: Type.Boolean(),
    createdByPrincipalId: UuidSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type FileSpaceV1 = Static<typeof FileSpaceSchema>;

export const ProjectFileSpaceBindingSchema = Type.Object(
  {
    schemaVersion: Type.Literal(FILE_SPACE_SCHEMA_VERSION),
    projectId: ProjectIdSchema,
    fileSpaceId: UuidSchema,
    isDefault: Type.Boolean(),
    boundByPrincipalId: UuidSchema,
    createdAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type ProjectFileSpaceBindingV1 = Static<typeof ProjectFileSpaceBindingSchema>;

/** Catalog projection: binding plus display name so clients need not list all projects. */
export const FileSpaceCatalogBindingSchema = Type.Object(
  {
    schemaVersion: Type.Literal(FILE_SPACE_SCHEMA_VERSION),
    projectId: ProjectIdSchema,
    fileSpaceId: UuidSchema,
    isDefault: Type.Boolean(),
    boundByPrincipalId: UuidSchema,
    createdAt: TimestampSchema,
    projectName: Type.String({ minLength: 1, maxLength: 256 })
  },
  { additionalProperties: false }
);
export type FileSpaceCatalogBindingV1 = Static<typeof FileSpaceCatalogBindingSchema>;

/** Knowledge Base metadata projected into a File Space catalog item. */
export const FileSpaceKnowledgeBaseSchema = Type.Object(
  {
    schemaVersion: Type.Literal(FILE_SPACE_SCHEMA_VERSION),
    knowledgeBaseId: UuidSchema,
    name: Type.String({ minLength: 1, maxLength: 256 }),
    description: Type.String({ maxLength: 4096 }),
    sourceFileSpaceId: UuidSchema,
    currentLeafConceptizationResultId: Type.Union([UuidSchema, Type.Null()]),
    currentHierarchySnapshotId: Type.Union([UuidSchema, Type.Null()]),
    revision: Type.Integer({ minimum: 0 }),
    createdBy: Type.String({ minLength: 1, maxLength: 256 }),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type FileSpaceKnowledgeBaseV1 = Static<typeof FileSpaceKnowledgeBaseSchema>;

export const FileSpaceCatalogItemSchema = Type.Object(
  {
    fileSpace: FileSpaceSchema,
    projectBindings: Type.Array(FileSpaceCatalogBindingSchema, { uniqueItems: true }),
    knowledgeBases: Type.Array(FileSpaceKnowledgeBaseSchema, { uniqueItems: true })
  },
  { additionalProperties: false }
);
export type FileSpaceCatalogItemV1 = Static<typeof FileSpaceCatalogItemSchema>;

export const FileSpaceCreateInputSchema = Type.Object(
  {
    fileSpaceId: UuidSchema,
    name: Type.String({ minLength: 1, maxLength: 256 }),
    automationEnabled: Type.Optional(Type.Boolean())
  },
  { additionalProperties: false }
);
export type FileSpaceCreateInputV1 = Static<typeof FileSpaceCreateInputSchema>;

export const ProjectFilePermissionReconciliationFailureSchema = Type.Object(
  {
    matrixUserId: Type.Union([Type.String({ minLength: 1, maxLength: 1024 }), Type.Null()]),
    principalId: Type.Union([UuidSchema, Type.Null()]),
    code: Type.String({ minLength: 1, maxLength: 128 })
  },
  { additionalProperties: false }
);
export type ProjectFilePermissionReconciliationFailureV1 = Static<
  typeof ProjectFilePermissionReconciliationFailureSchema
>;

export const ProjectFilePermissionReconciliationResultSchema = Type.Object(
  {
    fileSpaceId: UuidSchema,
    status: Type.Union([Type.Literal("completed"), Type.Literal("already_running")]),
    desiredHumanCount: Type.Integer({ minimum: 0 }),
    retainedPermissionCount: Type.Integer({ minimum: 0 }),
    grantedPermissionCount: Type.Integer({ minimum: 0 }),
    revokedPermissionCount: Type.Integer({ minimum: 0 }),
    releasedPermissionCount: Type.Integer({ minimum: 0 }),
    failures: Type.Array(ProjectFilePermissionReconciliationFailureSchema)
  },
  { additionalProperties: false }
);
export type ProjectFilePermissionReconciliationResultV1 = Static<
  typeof ProjectFilePermissionReconciliationResultSchema
>;

export const FileSpaceAutomationPutInputSchema = Type.Object(
  { enabled: Type.Boolean() },
  { additionalProperties: false }
);
export type FileSpaceAutomationPutInputV1 = Static<typeof FileSpaceAutomationPutInputSchema>;

/**
 * Set OpenCloud Drive quota for a File Space.
 * `totalBytes: null` means unlimited (OpenCloud total = 0).
 */
export const FileSpaceQuotaPutInputSchema = Type.Object(
  {
    totalBytes: Type.Union([
      Type.Integer({ minimum: 1, maximum: 9_007_199_254_740_991 }),
      Type.Null()
    ])
  },
  { additionalProperties: false }
);
export type FileSpaceQuotaPutInputV1 = Static<typeof FileSpaceQuotaPutInputSchema>;

export const ProjectFileSpaceBindingPutInputSchema = Type.Object(
  { isDefault: Type.Optional(Type.Boolean()) },
  { additionalProperties: false }
);
export type ProjectFileSpaceBindingPutInputV1 = Static<
  typeof ProjectFileSpaceBindingPutInputSchema
>;

export const ProjectDefaultFileSpacePutInputSchema = Type.Object(
  { fileSpaceId: UuidSchema },
  { additionalProperties: false }
);
export type ProjectDefaultFileSpacePutInputV1 = Static<
  typeof ProjectDefaultFileSpacePutInputSchema
>;

export const KnowledgeBaseSourceFileSpacePutInputSchema = Type.Object(
  {
    fileSpaceId: UuidSchema,
    expectedKnowledgeBaseRevision: Type.Integer({ minimum: 0 })
  },
  { additionalProperties: false }
);
export type KnowledgeBaseSourceFileSpacePutInputV1 = Static<
  typeof KnowledgeBaseSourceFileSpacePutInputSchema
>;

export const KnowledgeBaseSourceFileSpaceDeleteInputSchema = Type.Object(
  { expectedKnowledgeBaseRevision: Type.Integer({ minimum: 0 }) },
  { additionalProperties: false }
);
export type KnowledgeBaseSourceFileSpaceDeleteInputV1 = Static<
  typeof KnowledgeBaseSourceFileSpaceDeleteInputSchema
>;
